import { useState } from 'react';
import type { PaletteColors } from './avatarTypes';
import { PALETTE_PRESETS } from './avatarData';

interface ColorPickerProps {
  palette: PaletteColors;
  onChange: (palette: PaletteColors) => void;
}

const COLOR_KEYS: { key: keyof PaletteColors; label: string }[] = [
  { key: 'bodyPrimary', label: 'Body Primary' },
  { key: 'bodySecondary', label: 'Body Secondary' },
  { key: 'eyeColor', label: 'Eyes' },
  { key: 'mouthColor', label: 'Mouth' },
  { key: 'accessoryColor', label: 'Accessory' },
  { key: 'backgroundColor', label: 'Background' },
];

function SwatchRow({
  colors,
  selected,
  onSelect,
}: {
  colors: string[];
  selected: string;
  onSelect: (color: string) => void;
}) {
  return (
    <div className="malpedia-avatar-swatch-row">
      {colors.map((c) => (
        <button
          key={c}
          className={`malpedia-avatar-swatch${c === selected ? ' malpedia-avatar-swatch--selected' : ''}`}
          style={{ backgroundColor: c }}
          onClick={() => onSelect(c)}
          title={c}
        />
      ))}
    </div>
  );
}

const QUICK_COLORS = [
  '#f38ba8', '#eba0ac', '#fab387', '#f9e2af', '#a6e3a1',
  '#94e2d5', '#89b4fa', '#cba6f7', '#b4befe', '#f5c2e7',
  '#ff00ff', '#00ffff', '#00ff41', '#ffff00', '#ff4500',
  '#CCCCCC', '#666666', '#1e1e2e', '#313244', '#1A1A1A',
];

export function SingleColorPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (color: string) => void;
}) {
  const [inputValue, setInputValue] = useState(value);
  const [swatchOpen, setSwatchOpen] = useState(false);

  const handleInputChange = (val: string) => {
    setInputValue(val);
    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
      onChange(val);
    }
  };

  return (
    <div className="malpedia-avatar-color-field">
      <label className="malpedia-avatar-color-label">{label}</label>
      <div className="malpedia-avatar-color-controls">
        <button
          className="malpedia-avatar-color-swatch-btn"
          style={{ backgroundColor: value }}
          onClick={() => setSwatchOpen((v) => !v)}
          title="Pick color"
        />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onBlur={() => setInputValue(value)}
          className="malpedia-avatar-hex-input"
          placeholder="#000000"
          maxLength={7}
        />
      </div>
      {swatchOpen && (
        <div className="malpedia-avatar-swatch-popover">
          <div className="malpedia-avatar-swatch-grid">
            {QUICK_COLORS.map((c) => (
              <button
                key={c}
                className={`malpedia-avatar-swatch${c === value ? ' malpedia-avatar-swatch--selected' : ''}`}
                style={{ backgroundColor: c }}
                onClick={() => {
                  onChange(c);
                  setInputValue(c);
                  setSwatchOpen(false);
                }}
                title={c}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ColorPicker({ palette, onChange }: ColorPickerProps) {
  const [customizeOpen, setCustomizeOpen] = useState(false);

  return (
    <div className="malpedia-avatar-palette-picker">
      {/* Preset palette cards */}
      <div className="malpedia-avatar-palette-grid">
        {PALETTE_PRESETS.map((preset) => {
          const isSelected =
            preset.colors.bodyPrimary === palette.bodyPrimary &&
            preset.colors.backgroundColor === palette.backgroundColor;
          let cls = 'malpedia-avatar-palette-card';
          if (isSelected) cls += ' malpedia-avatar-palette-card--selected';
          return (
            <button
              key={preset.id}
              className={cls}
              onClick={() => onChange(preset.colors)}
            >
              <div className="malpedia-avatar-palette-swatches">
                {Object.values(preset.colors).map((c, i) => (
                  <span
                    key={i}
                    className="malpedia-avatar-palette-dot"
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
              <span className="malpedia-avatar-palette-name">{preset.name}</span>
            </button>
          );
        })}
      </div>

      {/* Customize Colors expand button */}
      <button
        className="malpedia-avatar-customize-btn"
        onClick={() => setCustomizeOpen((v) => !v)}
      >
        {customizeOpen ? '\u25B2 Hide Custom Colors' : '\u25BC Customize Colors'}
      </button>

      {/* Expandable per-color pickers */}
      {customizeOpen && (
        <div className="malpedia-avatar-color-advanced">
          {COLOR_KEYS.map(({ key, label }) => (
            <SingleColorPicker
              key={key}
              label={label}
              value={palette[key]}
              onChange={(color) => onChange({ ...palette, [key]: color })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
